import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import type { SurveyPcaDto, SurveySegmentationDto } from '@/shared/api/types'
import { components, getInstrument, listPolls, listVersions, segment } from './api'
import { PollSelect } from './PollSelect'
import { Pill, count, num, pct } from './vocab'

const SCALE_TYPES = new Set(['RATING_SCALE', 'LIKERT', 'NPS', 'NUMBER'])

/**
 * Clustering and components — the Multivariate tab, with its defects fixed.
 *
 * ## What was wrong with the original
 *
 * PollSphere clusters a raw 0–10 column against a 0–1 column, so Euclidean
 * distance is dominated by whichever question happens to have the widest range:
 * its segments are an artefact of scale, not of opinion. Its PCA runs on the
 * covariance matrix with the same consequence, and caps Jacobi at a hundred TOTAL
 * rotations — less than two sweeps on a twelve-variable matrix — then reports
 * whatever it had reached. Its centroid bars are scaled by a hardcoded range
 * table, and the analysed n and the displayed n are different numbers.
 *
 * ## What this shows instead
 *
 * The server standardises every feature, runs k-means from ten seeds and keeps
 * the best, reports centroids in the QUESTIONS' OWN UNITS as well as in z-scores,
 * runs Jacobi on the CORRELATION matrix to a tolerance, and returns the analysed
 * n as the only n there is. Every one of those is stated on the panel, because a
 * reader cannot check a method they cannot see.
 */
export function SegmentsPage() {
  const canAnalyse = useCan('survey:analyse')
  const [params, setParams] = useSearchParams()
  const pollId = params.get('poll')
  const [picked, setPicked] = useState<string[]>([])
  const [k, setK] = useState(3)
  const [clusters, setClusters] = useState<SurveySegmentationDto | null>(null)
  const [pca, setPca] = useState<SurveyPcaDto | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const versions = useLoad(
    () => (pollId ? listVersions(pollId) : Promise.resolve([])), [pollId])
  const versionId = (versions.data ?? []).slice()
    .sort((a, b) => b.versionNo - a.versionNo)[0]?.id ?? null
  const instrument = useLoad(
    () => (versionId ? getInstrument(versionId) : Promise.resolve(null)), [versionId])

  if (!canAnalyse) return <Forbidden what="segmentation" />

  const setPoll = (id: string) => {
    const next = new URLSearchParams(params)
    if (id) next.set('poll', id)
    else next.delete('poll')
    setParams(next, { replace: true })
    setPicked([])
    setClusters(null)
    setPca(null)
  }

  const numeric = (instrument.data?.questions ?? []).filter((q) => SCALE_TYPES.has(q.typeCode))

  const toggle = (id: string) => setPicked((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])

  async function run(what: 'segments' | 'components') {
    if (!pollId || picked.length < 2) return
    setBusy(true)
    setError(null)
    try {
      if (what === 'segments') setClusters(await segment(pollId, picked, k))
      else setPca(await components(pollId, picked))
    } catch (e) {
      setError(explain(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Segments"
        description="Standardised clustering and correlation-matrix components — with the method stated."
        right={<PollSelect polls={polls.data?.content ?? []} value={pollId} onChange={setPoll} />}
      />

      <ErrorStrip message={error ?? instrument.error} />

      {!pollId ? (
        <Note tone="info">Pick a poll.</Note>
      ) : instrument.loading ? <LoadingBlock label="Loading the instrument…" /> : (
        <>
          <Panel
            title="Choose the questions"
            description="Numeric questions only. Complete cases: an interview missing one of them is dropped rather than imputed."
          >
            {numeric.length < 2 ? (
              <Note tone="info">
                This instrument has fewer than two numeric questions, so there is nothing to
                cluster on. Rating scales, Likert items, NPS and plain numbers qualify.
              </Note>
            ) : (
              <>
                <div className="flex flex-wrap gap-1.5">
                  {numeric.map((q) => (
                    <button
                      key={q.id}
                      type="button"
                      className={`erp-btn-ghost h-8 text-erp-xs ${
                        picked.includes(q.id) ? 'bg-chocolate/10 text-chocolate' : ''}`}
                      onClick={() => toggle(q.id)}
                      title={q.text}
                    >
                      {q.code}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-end gap-3 mt-3">
                  <label className="flex items-center gap-1.5 text-erp-xs">
                    Segments
                    <input className="erp-input h-9 w-20 text-right tabular-nums" type="number"
                      min={2} max={12} value={k}
                      onChange={(e) => setK(Number(e.target.value) || 2)} />
                  </label>
                  <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                    disabled={busy || picked.length < 2} onClick={() => run('segments')}>
                    {busy ? 'Working…' : 'Cluster'}
                  </button>
                  <button type="button" className="erp-btn-secondary h-9 text-erp-sm"
                    disabled={busy || picked.length < 2} onClick={() => run('components')}>
                    Components
                  </button>
                  {picked.length > 0 && picked.length < 2 && (
                    <span className="text-erp-xs text-muted-foreground">Pick at least two.</span>
                  )}
                </div>
              </>
            )}
          </Panel>

          {clusters && (
            <Panel
              title={`${clusters.k} segments`}
              description={`${count(clusters.analysedN)} complete interviews · ${clusters.iterations} iteration${clusters.iterations === 1 ? '' : 's'}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Pill>{clusters.standardised ? 'features standardised' : 'raw features'}</Pill>
                <Pill>{clusters.converged ? 'converged' : 'did not converge'}</Pill>
              </div>
              {clusters.note && (
                <div className="mb-3"><Note tone="bad">{clusters.note}</Note></div>
              )}
              <div className="axis-table-scroll">
                <ErpTable head={(
                  <>
                    <Th>Segment</Th>
                    <Th className="text-right">Size</Th>
                    {clusters.featureCodes.map((code) => (
                      <Th key={code} className="text-right">{code}</Th>
                    ))}
                  </>
                )}>
                  {clusters.segments.map((s) => (
                    <tr key={s.index} className="hover:bg-muted/40">
                      <Td>Segment {s.index + 1}</Td>
                      <Td className="text-right tabular-nums">
                        {count(s.size)}
                        <span className="ml-1.5 text-erp-xs text-muted-foreground">
                          {pct(s.size / Math.max(1, clusters.analysedN), 0)}
                        </span>
                      </Td>
                      {s.centroid.map((value, i) => (
                        <Td key={i} className="text-right tabular-nums">
                          {num(value, 2)}
                          <span className="block text-erp-xs text-muted-foreground">
                            z {num(s.centroidZ[i] ?? null, 2)}
                          </span>
                        </Td>
                      ))}
                    </tr>
                  ))}
                </ErpTable>
              </div>
              <div className="mt-3">
                <Note tone="info">
                  Centroids are in each question's own units — "3.8 on a 1–5 scale" is readable
                  and "0.42 standard deviations" is not. The z-score is underneath for anybody
                  comparing across questions.
                </Note>
              </div>
            </Panel>
          )}

          {pca && (
            <Panel
              title="Principal components"
              description={`${count(pca.analysedN)} complete interviews · ${pca.sweeps} sweep${pca.sweeps === 1 ? '' : 's'}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Pill>
                  {pca.onCorrelationMatrix ? 'correlation matrix' : 'covariance matrix'}
                </Pill>
                <Pill>{pca.converged ? 'converged' : 'did not converge'}</Pill>
              </div>
              {pca.note && <div className="mb-3"><Note tone="bad">{pca.note}</Note></div>}
              <div className="axis-table-scroll">
                <ErpTable head={(
                  <>
                    <Th>Component</Th>
                    <Th className="text-right">Eigenvalue</Th>
                    <Th className="text-right">Variance</Th>
                    <Th className="text-right">Cumulative</Th>
                    {pca.featureCodes.map((code) => (
                      <Th key={code} className="text-right">{code}</Th>
                    ))}
                  </>
                )}>
                  {pca.components.map((c) => (
                    <tr key={c.index} className="hover:bg-muted/40">
                      <Td>PC{c.index + 1}</Td>
                      <Td className="text-right tabular-nums">{num(c.eigenvalue)}</Td>
                      <Td className="text-right tabular-nums">{pct(c.varianceShare)}</Td>
                      <Td className="text-right tabular-nums text-muted-foreground">
                        {pct(c.cumulativeShare)}
                      </Td>
                      {c.loadings.map((l, i) => (
                        <Td key={i} className="text-right tabular-nums">
                          <span className={Math.abs(Number(l ?? 0)) >= 0.5
                            ? 'text-chocolate font-medium' : ''}>
                            {num(l, 2)}
                          </span>
                        </Td>
                      ))}
                    </tr>
                  ))}
                </ErpTable>
              </div>
              <div className="mt-3">
                <Note tone="info">
                  These are LOADINGS — each feature's correlation with the component — and not
                  bare eigenvector elements. A loading beyond ±0.5 is what gives a component its
                  meaning.
                </Note>
              </div>
            </Panel>
          )}
        </>
      )}
    </div>
  )
}
